import React, { useMemo, useState, useEffect } from "react";
import { HiChevronDown } from "react-icons/hi";

export default function Faq({
  title,
  subtitle,
  tabs = [],
  faqData = {},
  defaultTab,
}) {
  // Pick initial tab safely
  const initialTab = useMemo(() => {
    if (defaultTab && tabs.some((t) => t.id === defaultTab)) return defaultTab;
    return tabs?.[0]?.id || "";
  }, [defaultTab, tabs]);

  const [activeTab, setActiveTab] = useState(initialTab);
  const [openIndex, setOpenIndex] = useState(null);

  // If tabs change dynamically, keep activeTab valid
  useEffect(() => {
    if (!activeTab && initialTab) setActiveTab(initialTab);
    if (activeTab && !tabs.some((t) => t.id === activeTab)) {
      setActiveTab(initialTab);
      setOpenIndex(null);
    }
  }, [tabs, activeTab, initialTab]);

  const list = faqData?.[activeTab] || [];

  const toggleFAQ = (i) => {
    setOpenIndex(openIndex === i ? null : i);
  };

  return (
    <section className="w-[90%] 2xl:w-[70%] mx-auto mt-24 lg:px-20 md:py-8">
      {/* Heading */}
      {(title || subtitle) && (
        <div className="flex flex-col items-center text-center">
          {title ? (
            <h2 className="h2-mb h2-md h2-lg h2-2xl">{title}</h2>
          ) : null}

          {subtitle ? (
            <p className="max-w-2xl text-gray-500 text-sm md:text-lg mt-5">
              {subtitle}
            </p>
          ) : null}
        </div>
      )}

      {/* Tabs */}
      {tabs?.length > 0 && (
        <div className="flex justify-center mt-6">
          <div className="flex gap-3 flex-wrap justify-center">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => {
                  setActiveTab(t.id);
                  setOpenIndex(null); // closes open question when changing tabs
                }}
                className={`rounded-full px-5 py-2.5 text-sm font-medium border transition-all h-10
                  ${
                    activeTab === t.id
                      ? "bg-[#0b2b40] text-white border-[#0b2b40]"
                      : "border-black/40 text-gray-900 hover:bg-black/5"
                  }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* FAQ List */}
      <div className="mt-10 flex flex-col">
        {list.map((item, i) => {
          const isOpen = openIndex === i;

          return (
            <div key={`${activeTab}-${i}`} className="w-full">
              <div
                className={`transition-all duration-300 rounded-2xl px-6 py-4 cursor-pointer
                  ${isOpen ? "bg-[#0b2b40]" : "bg-transparent"}`}
                onClick={() => toggleFAQ(i)}
              >
                <div className="flex items-center justify-between gap-4">
                  <h3
                    className={`font-medium text-lg transition-colors duration-300 
                      ${isOpen ? "text-white" : "text-gray-900"}`}
                  >
                    {item.question}
                  </h3>

                  <HiChevronDown
                    className={`shrink-0 text-2xl transform transition-transform duration-300
                      ${isOpen ? "rotate-180 text-white" : "rotate-0 text-gray-900"}`}
                  />
                </div>

                {isOpen && (
                  <p className="mt-4 text-white leading-relaxed pr-4">
                    {item.answer}
                  </p>
                )}
              </div>

              <hr className="border-black/10 my-2" />
            </div>
          );
        })}

        {/* Optional empty state */}
        {list.length === 0 && (
          <div className="text-center text-gray-500 py-10">
            Keine FAQs verfügbar.
          </div>
        )}
      </div>
    </section>
  );
}
